import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { observer } from 'mobx-react'

import { RentalDetailsCardModalTypes } from 'Modules/rentals/components/RentalDetailsCard/RentalDetailsCard.types'
import {
  getEarlyUpgradePath,
  getProlongPath,
  getRepairUpgradePath,
  getUpgradePath,
} from 'Modules/rentals/utils/rental.utils'

import { CardActions } from 'Components/CardActions/CardActions'

import { LostOrStolenModal } from 'Modals/LostOrStolenModal/LostOrStolenModal'
import { LostOrStolenModalTypesEnum } from 'Modals/LostOrStolenModal/LostOrStolenModal.types'
import { ReplaceBrokenDeviceModal } from 'Modals/ReplaceBrokenDeviceModal/ReplaceBrokenDeviceModal'

import { useStore } from 'Stores/index'
import { OriginRouteTypeEnum } from 'Stores/orderStore/orderStore.types'

import { useModal } from 'Hooks'

import {
  E2E_LOST_STOLEN_DEVICE_ACTION,
  E2E_ORDER_EARLY_UGPRADE_ACTION,
  E2E_ORDER_PROLONG_ACTION,
  E2E_ORDER_UPGRADE_ACTION,
  E2E_REPLACE_BROKEN_DEVICE_ACTION,
} from 'Constants/e2e'
import type { getRental_rental } from 'Constants/graphql/queries/detail/__generated__/getRental'

import { iconLostDevice } from 'Icons/iconLostDevice'
import { iconRepairDevice } from 'Icons/iconRepairDevice'
import { iconUpgradeDevice } from 'Icons/iconUpgradeDevice'

import type { DashboardDevicesActionsProps } from './DashboardDevicesActions.types'

export const DashboardDevicesActions = observer(
  (props: DashboardDevicesActionsProps) => {
    const { rental, refetch } = props
    const { orderStore } = useStore()
    const { setOriginRoute } = orderStore

    const navigate = useNavigate()
    const location = useLocation()
    const { employeeId } = useParams<{
      employeeId?: string
    }>()

    const { setModal } = useModal()

    const handleOpenReplaceBrokenDeviceModal = useCallback(
      (isReplaceFromDepot: boolean) => {
        setModal({
          component: ReplaceBrokenDeviceModal,
          isOpen: true,
          props: {
            isReplaceFromDepot,
            onSubmit: refetch,
            rental,
          },
        })
      },
      [refetch, rental, setModal],
    )

    const handleRepairUpgrade = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getRepairUpgradePath(
        false,
        rental as getRental_rental,
        employeeId,
      )

      navigate(path)
    }, [setOriginRoute, location.pathname, rental, employeeId, navigate])

    const handleOpenLostOrStolenModal = useCallback(() => {
      setModal({
        component: LostOrStolenModal,
        isOpen: true,
        props: {
          onSubmit: refetch,
          rental,
          type: LostOrStolenModalTypesEnum.EMPLOYEE,
        },
      })
    }, [refetch, rental, setModal])

    const handleEarlyUpgrade = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getEarlyUpgradePath(
        false,
        rental as getRental_rental,
        employeeId,
      )

      navigate(path)
    }, [employeeId, location.pathname, navigate, rental, setOriginRoute])

    const handleUpgrade = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getUpgradePath(false, rental as getRental_rental, employeeId)

      navigate(path)
    }, [employeeId, location.pathname, navigate, rental, setOriginRoute])

    const handleReplaceBrokenDevice = useCallback(
      (isReplaceFromDepot: boolean) => {
        const isRepairUpgradeAvailable =
          rental.actions.showRepairUpgradeAction &&
          rental.actions.showReplacementOrderFlow

        if (isRepairUpgradeAvailable) {
          handleRepairUpgrade()
        } else {
          handleOpenReplaceBrokenDeviceModal(isReplaceFromDepot)
        }
      },
      [
        rental.actions.showRepairUpgradeAction,
        rental.actions.showReplacementOrderFlow,
        handleRepairUpgrade,
        handleOpenReplaceBrokenDeviceModal,
      ],
    )

    const handleProlong = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getProlongPath(false, rental as getRental_rental, employeeId)

      navigate(path)
    }, [employeeId, location.pathname, navigate, rental, setOriginRoute])

    const actions = useMemo(
      () => [
        {
          e2eSelector: E2E_LOST_STOLEN_DEVICE_ACTION,
          icon: iconLostDevice,
          id: RentalDetailsCardModalTypes.LostOrStolenModal,
          isHidden: !rental.actions.showLostOrStolenAction,
          name: 'Device_lost_stolen',
          onActionClick: handleOpenLostOrStolenModal,
        },
        {
          e2eSelector: E2E_REPLACE_BROKEN_DEVICE_ACTION,
          icon: iconRepairDevice,
          id: RentalDetailsCardModalTypes.ReplaceBrokenDevice,
          isHidden: !rental.actions.showReplaceBrokenDeviceAction,
          name: 'Replace_broken_device',
          onActionClick: () => handleReplaceBrokenDevice(false),
        },
        {
          e2eSelector: E2E_ORDER_EARLY_UGPRADE_ACTION,
          icon: iconUpgradeDevice,
          id: RentalDetailsCardModalTypes.EarlyUpgradeDevice,
          isHidden: !rental.actions.showEarlyUpgradeAction,
          name: 'Early_upgrade_device',
          onActionClick: handleEarlyUpgrade,
        },
        {
          e2eSelector: E2E_ORDER_PROLONG_ACTION,
          icon: iconUpgradeDevice,
          id: RentalDetailsCardModalTypes.ProlongDevice,
          isHidden: !rental.actions.showDeviceProlongingAction,
          name: 'Extend_rental',
          onActionClick: handleProlong,
        },
        {
          e2eSelector: E2E_ORDER_UPGRADE_ACTION,
          icon: iconUpgradeDevice,
          id: RentalDetailsCardModalTypes.RentalCompleteUpgradeDevice,
          isHidden: !rental.actions.showStandardUpgradeAction,
          name: 'Upgrade_device',
          onActionClick: handleUpgrade,
        },
      ],
      [
        rental.actions.showLostOrStolenAction,
        rental.actions.showReplaceBrokenDeviceAction,
        rental.actions.showEarlyUpgradeAction,
        rental.actions.showDeviceProlongingAction,
        rental.actions.showStandardUpgradeAction,
        handleOpenLostOrStolenModal,
        handleReplaceBrokenDevice,
        handleEarlyUpgrade,
        handleProlong,
        handleUpgrade,
      ],
    )

    return (
      <CardActions actions={actions} customColumnsCount={4} type="rental" />
    )
  },
)
